/**
 * CL-CART-01 — global cart state.
 *
 * The reducer holds the cart shape from src/util/custom/cart.js and nothing else.
 * Persistence is a side effect driven by the thunks below, so the reducer stays pure and
 * testable.
 *
 * Storage strategy (D-1): localStorage always, plus a mirror into the buyer's own
 * privateData once they are logged in. On login the two are merged rather than one
 * overwriting the other (E14), and the result is stamped with the owner (D-1b).
 */

import { createSlice } from '@reduxjs/toolkit';
import {
  emptyCart,
  addItem,
  removeItem,
  setQuantity,
  clearSellerGroup,
  totalItemCount,
  getSellerGroup,
  reconcileCarts,
  claimCart,
  sanitizeCart,
  lockSellerGroup,
  unlockSellerGroup,
  isSellerGroupLocked,
  mergeSellerGroupItems,
  removeSellerGroupItems,
} from '../util/custom/cart';
import { loadCart, saveCart, clearStoredCart, CART_STORAGE_KEY } from '../util/custom/cartStorage';
import { types as sdkTypes } from '../util/sdkLoader';
import { OFFER_CLOSED_STATES } from '../util/custom/offers';

const { UUID } = sdkTypes;

const CART_PROFILE_KEY = 'cart';

// ================ Slice ================ //

const initialState = {
  cart: emptyCart(),
  initialized: false,
};

const cartSlice = createSlice({
  name: 'cart',
  initialState,
  reducers: {
    cartReplaced: (state, action) => {
      state.cart = action.payload;
      state.initialized = true;
    },
    itemAdded: (state, action) => {
      state.cart = addItem(state.cart, action.payload);
    },
    itemRemoved: (state, action) => {
      const { authorId, listingId, nowIso } = action.payload;
      state.cart = removeItem(state.cart, authorId, listingId, nowIso);
    },
    quantitySet: (state, action) => {
      const { authorId, listingId, quantity, nowIso } = action.payload;
      state.cart = setQuantity(state.cart, authorId, listingId, quantity, nowIso);
    },
    sellerGroupCleared: (state, action) => {
      const { authorId, nowIso } = action.payload;
      state.cart = clearSellerGroup(state.cart, authorId, nowIso);
    },
    // CL-NEG-01 requirement 13 — a seller group with an open offer is read-only.
    sellerGroupLocked: (state, action) => {
      const { authorId, transactionId, nowIso } = action.payload;
      state.cart = lockSellerGroup(state.cart, authorId, transactionId, nowIso);
    },
    sellerGroupUnlocked: (state, action) => {
      const { authorId, nowIso } = action.payload;
      state.cart = unlockSellerGroup(state.cart, authorId, nowIso);
    },
    // Recreate cart (requirement 15) rebuilds ONE seller's rows. `cartReplaced` would take the
    // whole cart with it, discarding every other seller's basket; a wholesale replace of the
    // group would discard whatever the offer's own snapshot never mentioned (review fix — see
    // mergeSellerGroupItems).
    sellerGroupItemsMerged: (state, action) => {
      const { authorId, items, supersededListingIds, nowIso } = action.payload;
      state.cart = mergeSellerGroupItems(state.cart, authorId, items, nowIso, supersededListingIds);
    },
    // A paid offer removes the rows it actually bought, never the whole group (review fix).
    sellerGroupItemsRemoved: (state, action) => {
      const { authorId, listingIds, nowIso } = action.payload;
      state.cart = removeSellerGroupItems(state.cart, authorId, listingIds, nowIso);
    },
  },
});

// ================ Exports ================ //

export const {
  cartReplaced,
  itemAdded,
  itemRemoved,
  quantitySet,
  sellerGroupCleared,
  sellerGroupLocked,
  sellerGroupUnlocked,
  sellerGroupItemsMerged,
  sellerGroupItemsRemoved,
} = cartSlice.actions;

export default cartSlice.reducer;

// ================ Selectors ================ //

export const selectCart = state => state.cart.cart;
export const selectCartInitialized = state => state.cart.initialized;
export const selectCartItemCount = state => totalItemCount(state.cart.cart);
export const selectSellerGroup = (state, authorId) => getSellerGroup(state.cart.cart, authorId);

// ================ Thunks ================ //

const nowIso = () => new Date().toISOString();

const currentUserId = getState => getState().user?.currentUser?.id?.uuid || null;

const profileCart = getState =>
  getState().user?.currentUser?.attributes?.profile?.privateData?.[CART_PROFILE_KEY] || null;

const persist = getState => {
  saveCart(selectCart(getState()));
};

/**
 * Read the cart from localStorage and, if the buyer is logged in, merge it with the copy
 * on their profile so the cart follows them across devices. Neither side is discarded
 * (E14).
 *
 * `loadCart` is given the current user id on purpose: that is where the D-1b ownership
 * check happens, so a cart left by somebody else is dropped rather than merged. The
 * merged result is then claimed, or it would stay unowned and the check would have
 * nothing to compare against next time.
 */
export const initializeCart = () => (dispatch, getState, sdk) => {
  const userId = currentUserId(getState);
  const now = nowIso();
  const local = loadCart(now, userId);
  // The profile copy goes through the SAME gate as the stored one. It was previously
  // handed straight to mergeCarts, so a cart from an old schema, one months past the
  // retention window, or one over the per-seller cap merged in unchecked — two doors into
  // the same room, one of them locked.
  const remote = sanitizeCart(profileCart(getState), { nowIso: now, currentUserId: userId });

  // reconcileCarts, not mergeCarts: a union is only correct when a GUEST cart meets a saved
  // one (E14). For the same buyer on a second device the local copy is merely stale, and a
  // union resurrects whatever they deleted elsewhere — then this device writes it back and
  // undoes the deletion for good. See reconcileCarts for the ownerId rule.
  const reconciled = reconcileCarts(local, remote, userId);
  const merged = userId ? claimCart(reconciled, userId) : reconciled;
  dispatch(cartReplaced(merged));
  saveCart(merged);

  return userId ? dispatch(syncCartToProfile()) : Promise.resolve(merged);
};

/**
 * Mirror the cart onto the buyer's own profile. privateData is readable only by its
 * owner, so nobody else can see what is in someone's cart.
 *
 * One key, not the whole object: privateData merges at the top level, so writing `cart`
 * alone leaves CL-STOCK-01's `stockLocations` on the same profile untouched (SL11).
 */
export const syncCartToProfile = () => (dispatch, getState, sdk) => {
  const userId = currentUserId(getState);
  if (!userId) return Promise.resolve();

  const cart = selectCart(getState());

  // The gate, not a second copy of the caller's job. A cart still carrying `ownerId: null`
  // (or somebody else's id) has NOT been merged with this buyer's saved cart, so writing it
  // would overwrite rows added on another device. Callers are supposed to run
  // initializeCart first; this makes forgetting one harmless instead of destructive, which
  // is what a session that starts without a page reload — login, IdP signup — already cost
  // us once.
  if (cart.ownerId !== userId) return Promise.resolve();

  // Nothing to mirror, and nothing mirrored yet: skip the write. Otherwise every logged-in
  // user writes an empty cart on every page load, stamping our key onto the profile of
  // every seller who never opens a cart.
  //
  // `updatedAt` is the load-bearing half, and it is here because `stored` alone got this
  // wrong. `profileCart` reads the currentUser fetched at BOOT, and our own writes never
  // refresh it — so for a buyer whose profile held no cart, this session's add wrote the
  // row, the matching remove left `stored` still null, and the write that would have
  // recorded the deletion was skipped. The profile kept the row, the next device merged it
  // back in and wrote it back: the same resurrection reconcileCarts exists to prevent,
  // arriving through a stale read instead of a union.
  //
  // The question the guard actually wants to ask is "has this cart ever been touched?", and
  // the cart answers that itself: emptyCart() has updatedAt null and every mutation stamps
  // it. That cannot go stale.
  const stored = profileCart(getState);
  if (!stored && totalItemCount(cart) === 0 && !cart.updatedAt) return Promise.resolve();

  return sdk.currentUser.updateProfile({ privateData: { [CART_PROFILE_KEY]: cart } }).catch(() => {
    // A failed mirror must never block the buyer: localStorage still holds the cart and
    // the next successful sync catches up.
  });
};

/** Clear the cart when a user logs out (D-1a). Called from src/reducers.js. */
export const clearCartOnLogout = () => {
  clearStoredCart();
};

export const addListingToCart = ({ listing, quantity = 1, maxQuantity }) => (
  dispatch,
  getState
) => {
  if (!listing?.id?.uuid || !listing?.author?.id?.uuid) return Promise.resolve();

  const images = listing.images || [];
  // The caller should pass maxQuantity, because only it knows the listing type's
  // stockType — `infiniteOneItem` has endless stock but still allows one per order. This
  // fallback exists so a caller that forgets still cannot exceed the stock that actually
  // exists, which is what AC-3 is protecting against.
  const stock = listing.currentStock?.attributes?.quantity;
  const cap = maxQuantity != null ? maxQuantity : stock != null ? stock : null;

  dispatch(
    itemAdded({
      listingId: listing.id.uuid,
      authorId: listing.author.id.uuid,
      authorDisplayName: listing.author.attributes?.profile?.displayName,
      quantity,
      maxQuantity: cap,
      titleSnapshot: listing.attributes.title,
      priceSnapshotInSubunits: listing.attributes.price?.amount,
      currencySnapshot: listing.attributes.price?.currency,
      imageSnapshotUrl: images[0]?.attributes?.variants?.['listing-card']?.url,
      nowIso: nowIso(),
    })
  );

  persist(getState);
  return dispatch(syncCartToProfile());
};

/**
 * Remove a row, and RESOLVE WITH THE ROW so the caller can offer Undo (AC-12).
 *
 * The row has to be read before the reducer runs — afterwards it is gone, and rebuilding it
 * from the live listing would lose the snapshots and the quantity the buyer had chosen.
 *
 * The seller's display name travels WITH it, because it is the one thing Undo needs that a
 * row has never carried: the name lives on the group, and removing the last row deletes the
 * group outright. Without it a restore rebuilt the group nameless.
 */
export const removeListingFromCart = ({ authorId, listingId }) => (dispatch, getState) => {
  const group = getSellerGroup(selectCart(getState()), authorId);
  const row = group?.items?.find(i => i.listingId === listingId) || null;
  const removed = row ? { ...row, authorDisplayName: group.authorDisplayName } : null;

  dispatch(itemRemoved({ authorId, listingId, nowIso: nowIso() }));
  persist(getState);
  return dispatch(syncCartToProfile()).then(() => removed);
};

/**
 * Put a removed row back exactly as it was (AC-12's Undo).
 *
 * `itemAdded` rather than a new action: the row is gone, so addItem creates it instead of
 * incrementing, and the cap and timestamp rules apply on the way in like any other add.
 *
 * The row spread here carries the seller's display name that removeListingFromCart attached,
 * which is what lets addItem rebuild a group that was deleted with the buyer's last item.
 * addItem builds the row field by field, so the name reaches the group and not the row.
 */
export const restoreCartItem = ({ authorId, item }) => (dispatch, getState) => {
  if (!authorId || !item?.listingId) return Promise.resolve();

  dispatch(itemAdded({ ...item, authorId, nowIso: nowIso() }));
  persist(getState);
  return dispatch(syncCartToProfile());
};

/**
 * E13 — two tabs share one cart. Re-read the stored copy so the header count cannot be
 * stale after another tab changes it.
 *
 * Deliberately NOT initializeCart: that mirrors to the profile, and regaining focus is not
 * a reason to spend an API write. Storage is already the shared copy between tabs.
 */
export const reloadCartFromStorage = () => (dispatch, getState) => {
  const userId = currentUserId(getState);
  const stored = loadCart(nowIso(), userId);
  // Claimed, exactly as initializeCart does. Without this a reload hands a logged-in buyer
  // an unowned cart, and syncCartToProfile's ownership gate then refuses every later write
  // — the cart would keep working in this tab and silently stop following them anywhere.
  const next = userId ? claimCart(stored, userId) : stored;

  // Only on a real difference. A focus event that changes nothing should not re-render the
  // header, and must not look like a cart change to anything watching.
  if (JSON.stringify(next) === JSON.stringify(selectCart(getState()))) return;
  dispatch(cartReplaced(next));
};

/**
 * Wire E13 up. Returns an unsubscribe function.
 *
 * Two events, because neither covers the other: `storage` fires in the OTHER tabs the
 * moment one writes, and `visibilitychange` catches a tab that was hidden while the write
 * happened in a tab that has since closed.
 */
export const watchCartStorage = () => dispatch => {
  if (typeof window === 'undefined') return () => {};

  const reload = () => dispatch(reloadCartFromStorage());
  // e.key is null when storage is cleared wholesale, which also concerns us.
  const onStorage = e => {
    if (!e.key || e.key === CART_STORAGE_KEY) reload();
  };
  const onVisibility = () => {
    if (document.visibilityState === 'visible') reload();
  };

  window.addEventListener('storage', onStorage);
  document.addEventListener('visibilitychange', onVisibility);

  return () => {
    window.removeEventListener('storage', onStorage);
    document.removeEventListener('visibilitychange', onVisibility);
  };
};

export const setCartQuantity = ({ authorId, listingId, quantity }) => (dispatch, getState) => {
  dispatch(quantitySet({ authorId, listingId, quantity, nowIso: nowIso() }));
  persist(getState);
  return dispatch(syncCartToProfile());
};

export const clearSellerCart = ({ authorId }) => (dispatch, getState) => {
  dispatch(sellerGroupCleared({ authorId, nowIso: nowIso() }));
  const cart = selectCart(getState());
  if (totalItemCount(cart) === 0) {
    clearStoredCart();
  } else {
    saveCart(cart);
  }
  return dispatch(syncCartToProfile());
};

/**
 * CL-NEG-01 requirement 13 — an open offer freezes this ONE seller's group. NOT because the offer
 * holds that stock: under D12 it holds nothing. Because the offer is PRICED from a frozen
 * `protectedData.cart` snapshot (offer-initiate.js writes it, runPaymentJoin re-reads it), so a
 * basket edited underneath a live negotiation would make this page and the offer describe two
 * different orders — and "Change items" would silently mean "make a different offer".
 * Called from BuyerOfferPage.duck.js the moment a transaction id exists, whichever path
 * produced it (a fresh offer or an idempotent replay — a replay's negotiation is priced from the
 * same snapshot).
 */
export const lockCartForOffer = ({ authorId, transactionId }) => (dispatch, getState) => {
  dispatch(sellerGroupLocked({ authorId, transactionId, nowIso: nowIso() }));
  persist(getState);
  return dispatch(syncCartToProfile());
};

export const releaseCartLock = ({ authorId }) => (dispatch, getState) => {
  dispatch(sellerGroupUnlocked({ authorId, nowIso: nowIso() }));
  persist(getState);
  return dispatch(syncCartToProfile());
};

/** Recreate cart (requirement 15): restore one seller's rows from a frozen transaction snapshot. */
export const mergeSellerCartItems = ({ authorId, items, supersededListingIds }) => (
  dispatch,
  getState
) => {
  dispatch(sellerGroupItemsMerged({ authorId, items, supersededListingIds, nowIso: nowIso() }));
  persist(getState);
  return dispatch(syncCartToProfile());
};

/**
 * A paid offer's rows leave the basket — and ONLY those rows.
 *
 * `clearSellerCart` stays the right call for basket checkout, where the whole group is what was
 * bought. An offer's snapshot is not always the buyer's basket (a listing-mode offer snapshots
 * one row while the buyer may hold three more with the same seller), so the paid path names what
 * it bought. Storage is cleared outright when nothing is left, the same rule clearSellerCart
 * applies — an empty cart should not be a stored object.
 */
export const removePurchasedCartItems = ({ authorId, listingIds }) => (dispatch, getState) => {
  dispatch(sellerGroupItemsRemoved({ authorId, listingIds, nowIso: nowIso() }));
  const cart = selectCart(getState());
  if (totalItemCount(cart) === 0) {
    clearStoredCart();
  } else {
    saveCart(cart);
  }
  return dispatch(syncCartToProfile());
};

/**
 * Clear a lock whose negotiation is over.
 *
 * The CL-CART-01 Milestone C re-plan chose option A — store the transaction id — and named its
 * one cost: "a stale lock survives if the offer ends in another browser". This is the reconcile
 * it asked for. The availability re-check cannot answer it (/api/cart-availability knows about
 * listings, not transactions), so the transaction is asked directly: one `transactions.show` per
 * LOCKED group, which is at most one request on a page that already makes one.
 *
 * A transaction that cannot be read at all (deleted, or no longer ours) also unlocks: a lock
 * pointing at nothing is a cart the buyer can never edit again.
 */
export const verifyCartLocks = () => (dispatch, getState, sdk) => {
  const cart = selectCart(getState());
  const locked = Object.values(cart?.sellers || {}).filter(isSellerGroupLocked);
  return Promise.all(
    locked.map(group =>
      sdk.transactions
        .show({ id: new UUID(group.lockedByTransactionId) })
        .then(response => {
          const state = response?.data?.data?.attributes?.state;
          const isOver = OFFER_CLOSED_STATES.includes(state);
          return isOver ? dispatch(releaseCartLock({ authorId: group.authorId })) : null;
        })
        .catch(() => dispatch(releaseCartLock({ authorId: group.authorId })))
    )
  );
};
